import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { SYMBOL_DATA, SP500_SYMBOLS, INTERVALS, PERIODS } from '../constants/symbols';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AVAILABLE_SYMBOLS = new Set(SYMBOL_DATA.map(item => item.symbol.toUpperCase()));

const ScannerMenu = ({ onScanComplete }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    symbols: ['AAPL'],
    period: '1d',
    interval: '5m',
    account_size: 10000,
    risk_pct: 1
  });
  const [loading, setLoading] = useState(false);
  const [scanProgress, setScanProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchSymbol, setSearchSymbol] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false); // Default closed on mobile
  
  // Saved symbol lists state
  const [savedLists, setSavedLists] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [showSavedLists, setShowSavedLists] = useState(false);

  // Fetch saved lists on component mount
  React.useEffect(() => {
    fetchSavedLists();
  }, []);

  const fetchSavedLists = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/saved-symbols`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedLists(response.data);
    } catch (err) {
      console.error('Failed to fetch saved lists:', err);
    }
  };

  const handleSaveList = async () => {
    if (!newListName.trim()) {
      alert(t('enter_list_name_alert'));
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/saved-symbols`,
        {
          name: newListName,
          symbols: formData.symbols
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess(t('save_list_success', { name: newListName, count: formData.symbols.length }));
      setNewListName('');
      setShowSaveModal(false);
      fetchSavedLists();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError((Array.isArray(err.response?.data?.detail) ? err.response.data.detail.map(e => e.msg || String(e)).join('; ') : err.response?.data?.detail) || t('save_list_failed'));
    }
  };

  const handleLoadList = (savedList) => {
    setFormData(prev => ({ ...prev, symbols: savedList.symbols }));
    setSuccess(t('load_list_success', { name: savedList.name, count: savedList.symbol_count }));
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleDeleteList = async (listId, listName) => {
    if (!window.confirm(t('confirm_delete_list', { name: listName }))) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/saved-symbols/${listId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess(t('delete_list_success', { name: listName }));
      fetchSavedLists();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError((Array.isArray(err.response?.data?.detail) ? err.response.data.detail.map(e => e.msg || String(e)).join('; ') : err.response?.data?.detail) || t('delete_list_failed'));
    }
  };

  const handleRunSavedList = async (savedList) => {
    setFormData(prev => ({ ...prev, symbols: savedList.symbols }));
    setSuccess(t('run_saved_list_initiated', { name: savedList.name }));
    
    // Trigger scan after a short delay
    setTimeout(() => {
      handleSubmit(new Event('submit'));
    }, 500);
  };

  // Enhanced filtering: search both symbol and company name
  const filteredSymbols = () => {
    const rawSearch = searchSymbol.trim();
    if (!rawSearch) {
      return SYMBOL_DATA; // Show all symbols when no search
    }

    // Comma-separated mode: treat entries as explicit symbol list.
    if (rawSearch.includes(',')) {
      const symbolSet = new Set(
        rawSearch
          .split(',')
          .map(term => term.trim().toUpperCase())
          .filter(Boolean)
      );

      if (!symbolSet.size) {
        return SYMBOL_DATA;
      }

      return SYMBOL_DATA.filter(item => symbolSet.has(item.symbol.toUpperCase()));
    }

    const searchLower = rawSearch.toLowerCase();
    
    // Search in SYMBOL_DATA (with names)
    const matches = SYMBOL_DATA.filter(item =>
      item.symbol.toLowerCase().includes(searchLower) ||
      item.name.toLowerCase().includes(searchLower)
    );
    
    return matches; // Return all matches (no limit)
  };

  const handleSymbolToggle = (symbol) => {
    setFormData(prev => {
      const symbols = prev.symbols.includes(symbol)
        ? prev.symbols.filter(s => s !== symbol) // Remove if already selected
        : [...prev.symbols, symbol]; // Add if not selected
      
      return { ...prev, symbols: symbols.length > 0 ? symbols : ['AAPL'] }; // Keep at least one
    });
  };

  const handleSelectAll = () => {
    const allSymbols = SP500_SYMBOLS; // Select all S&P 500 symbols (500+)
    setFormData(prev => ({ ...prev, symbols: allSymbols }));
    setSuccess(t('select_all_success', { count: allSymbols.length }));
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleClearAll = () => {
    setFormData(prev => ({ ...prev, symbols: ['AAPL'] }));
  };

  // Check if all filtered symbols are selected
  const areAllFilteredSelected = () => {
    const filtered = filteredSymbols();
    if (filtered.length === 0) return false;
    return filtered.every(item => formData.symbols.includes(item.symbol));
  };

  // Check if some but not all filtered symbols are selected
  const areSomeFilteredSelected = () => {
    const filtered = filteredSymbols();
    if (filtered.length === 0) return false;
    const selectedCount = filtered.filter(item => formData.symbols.includes(item.symbol)).length;
    return selectedCount > 0 && selectedCount < filtered.length;
  };

  // Handle master checkbox toggle
  const handleMasterCheckbox = () => {
    const filtered = filteredSymbols();
    if (areAllFilteredSelected()) {
      // Deselect all filtered symbols
      const filteredSymbolsList = filtered.map(item => item.symbol);
      setFormData(prev => ({
        ...prev,
        symbols: prev.symbols.filter(s => !filteredSymbolsList.includes(s)).length > 0
          ? prev.symbols.filter(s => !filteredSymbolsList.includes(s))
          : ['AAPL'] // Keep at least one symbol
      }));
    } else {
      // Select all filtered symbols
      const filteredSymbolsList = filtered.map(item => item.symbol);
      const newSymbols = [...new Set([...formData.symbols, ...filteredSymbolsList])];
      setFormData(prev => ({ ...prev, symbols: newSymbols }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'lookback' || name === 'account_size' || name === 'risk_pct' 
        ? parseFloat(value) || 0 
        : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');

      if (formData.symbols.length === 1) {
        // Single symbol – use the original endpoint directly.
        setScanProgress({ done: 0, total: 1 });
        const response = await axios.post(
          `${API}/scanner/run`,
          { ...formData, symbol: formData.symbols[0] },
          { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
        );
        setScanProgress({ done: 1, total: 1 });
        const result = response.data;
        const totalSignals = result.signals?.length ?? 0;
        if (totalSignals > 0) {
          setSuccess(t('scan_summary_with_signals', {
            total: 1,
            signals: totalSignals,
            summary: `${formData.symbols[0]}(${totalSignals})`,
          }));
        } else {
          setSuccess(t('scan_summary_no_signals', { total: 1 }));
        }
      } else {
        // Multiple symbols – use batch endpoint (concurrent, much faster).
        const total = formData.symbols.length;
        setScanProgress({ done: 0, total });

        // Animate a progress counter while the batch runs server-side.
        // Estimate ~2 s per symbol ÷ concurrency=8, capped at 90% until done.
        const concurrency = 8;
        const msPerBatch = 2200;
        const tickMs = Math.max(200, Math.round((msPerBatch / concurrency) * 100) / 100);
        let estimated = 0;
        const timer = setInterval(() => {
          estimated = Math.min(estimated + concurrency, Math.floor(total * 0.9));
          setScanProgress(p => ({ ...p, done: estimated }));
        }, tickMs);

        let batch;
        try {
          const response = await axios.post(
            `${API}/scanner/run-batch`,
            {
              symbols: formData.symbols,
              period: formData.period,
              interval: formData.interval,
              account_size: formData.account_size,
              risk_pct: formData.risk_pct,
              concurrency: 8,
            },
            { headers: { Authorization: `Bearer ${token}` }, timeout: 300000 }
          );
          batch = response.data;
        } finally {
          clearInterval(timer);
          setScanProgress({ done: total, total });
        }

        if (batch.signals_found > 0) {
          const symbolsSummary = (batch.results || [])
            .filter(r => r.signals?.length > 0)
            .map(r => `${r.symbol}(${r.signals.length})`)
            .join(', ');
          setSuccess(t('scan_summary_with_signals', {
            total: batch.scanned,
            signals: batch.signals_found,
            summary: symbolsSummary,
          }));
        } else {
          setSuccess(t('scan_summary_no_signals', { total: batch.scanned }));
        }
      }

      // Notify parent to refresh results
      if (onScanComplete) {
        onScanComplete();
      }
    } catch (err) {
      setError((Array.isArray(err.response?.data?.detail) ? err.response.data.detail.map(e => e.msg || String(e)).join('; ') : err.response?.data?.detail) || t('scan_failed'));
    } finally {
      setLoading(false);
      setScanProgress({ done: 0, total: 0 });
    }
  };

  const notFoundSymbols = (() => {
    const rawSearch = searchSymbol.trim();
    if (!rawSearch.includes(',')) return [];

    const requestedSymbols = rawSearch
      .split(',')
      .map(term => term.trim().toUpperCase())
      .filter(Boolean);

    if (!requestedSymbols.length) return [];

    return [...new Set(requestedSymbols.filter(symbol => !AVAILABLE_SYMBOLS.has(symbol)))];
  })();

  return (
    <div className="relative w-0 lg:w-80 xl:w-96 flex-shrink-0">
      {/* Toggle Button for Mobile - Fixed at Top */}
      <button
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className="lg:hidden fixed left-4 z-50 bg-blue-600 text-white p-3 rounded-full shadow-2xl hover:bg-blue-700 transition-all duration-200 flex items-center justify-center"
        style={{
          width: '48px',
          height: '48px',
          top: 'calc(var(--app-header-height, 0px) + 1rem)'
        }}
      >
        <span className="text-xl font-bold">
          {isMenuOpen ? '✕' : '☰'}
        </span>
      </button>

      {/* Sidebar */}
      <div 
        className={`
          fixed lg:sticky top-0 left-0 h-screen bg-white shadow-xl z-40
          transition-transform duration-300 ease-in-out
          ${isMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          w-full sm:w-96 lg:w-80 xl:w-96 flex flex-col max-w-md
        `}
      >
        <div className="relative flex flex-col h-full min-h-0">
          {/* Header + Primary Action */}
          <div className="sticky top-0 z-30 bg-white px-6 pt-6 pb-4 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <h2 className="text-xl font-bold text-gray-800">
                {t('scanner_options')}
              </h2>
              <button
                onClick={() => setIsMenuOpen(false)}
                className="lg:hidden text-gray-600 hover:text-gray-800 p-2"
              >
                <span className="text-2xl font-bold">✕</span>
              </button>
            </div>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="mt-4 w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition font-semibold text-lg shadow-lg"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a 8 8 0 0 18-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A 7.962 7.962 0 0 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {scanProgress.total > 1
                    ? `${t('running_scan')} ${scanProgress.done}/${scanProgress.total}`
                    : t('running_scan')}
                </span>
              ) : (
                `🚀 ${t('run_scanner')}`
              )}
            </button>
          </div>

          {/* Scrollable Form Content */}
          <div className="flex-1 overflow-y-auto px-6 pb-6 min-h-0">
            <div className="space-y-4 pt-4">
              {/* Success Message */}
              {success && (
                <div className="bg-green-100 text-green-800 p-3 rounded-lg text-sm">
                  {success}
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="bg-red-100 text-red-800 p-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

            {/* Symbol Selection */}
            <div>
              
              {/* Action Buttons */}
              <div className="flex gap-2 mb-2">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="flex-1 px-3 py-2 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition"
                >
                  {`${t('select_all')} (${SP500_SYMBOLS.length})`}
                </button>
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="flex-1 px-3 py-2 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition"
                >
                  {t('clear_all')}
                </button>
              </div>

              {/* Save and Load Buttons */}
              <div className="flex gap-2 mb-2">
                <button
                  type="button"
                  onClick={() => setShowSaveModal(true)}
                  className="flex-1 px-3 py-2 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition"
                  disabled={formData.symbols.length === 0}
                >
                  {`💾 ${t('save_current')}`}
                </button>
                <button
                  type="button"
                  onClick={() => setShowSavedLists(!showSavedLists)}
                  className="flex-1 px-3 py-2 bg-purple-500 text-white text-xs rounded hover:bg-purple-600 transition"
                >
                  {`📋 ${t('saved_lists')} (${savedLists.length})`}
                </button>
              </div>

              {/* Saved Lists Dropdown */}
              {showSavedLists && savedLists.length > 0 && (
                <div className="mb-2 border border-purple-300 rounded-lg p-3 bg-purple-50 max-h-48 overflow-y-auto">
                  <div className="text-xs font-semibold text-purple-800 mb-2">{t('saved_lists_heading')}</div>
                  {savedLists.map(list => (
                    <div key={list.list_id} className="flex items-center justify-between py-2 border-b border-purple-200 last:border-0">
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-gray-800">{list.name}</div>
                        <div className="text-xs text-gray-600">{t('symbol_count_label', { count: list.symbol_count })}</div>
                      </div>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => handleLoadList(list)}
                          className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                          title={t('load_symbols_tooltip')}
                        >
                          {t('load')}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRunSavedList(list)}
                          className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600"
                          title={t('run_saved_list_tooltip')}
                        >
                          {t('run_now')}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteList(list.list_id, list.name)}
                          className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                          title={t('delete_list_tooltip')}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* Search Box */}
              <input
                type="text"
                placeholder={t('search_by_symbol')}
                value={searchSymbol}
                onChange={(e) => setSearchSymbol(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-2 focus:ring-2 focus:ring-blue-500"
              />

              {notFoundSymbols.length > 0 && (
                <div className="mb-2 p-2 border border-red-200 bg-red-50 rounded-lg">
                  <div className="text-xs font-semibold text-red-700 mb-1">Not found:</div>
                  <div className="flex flex-wrap gap-1">
                    {notFoundSymbols.map((symbol) => (
                      <span
                        key={`missing-${symbol}`}
                        className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700 border border-red-200"
                      >
                        {symbol} (not found)
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Symbol List with Checkboxes */}
              <div className="border border-gray-300 rounded-lg max-h-48 overflow-y-auto">
                {filteredSymbols().length > 0 ? (
                  <>
                    <div className="sticky top-0 bg-gray-50 px-3 py-2 text-xs border-b flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={areAllFilteredSelected()}
                          ref={(el) => {
                            if (el) {
                              el.indeterminate = areSomeFilteredSelected();
                            }
                          }}
                          onChange={handleMasterCheckbox}
                          className="w-4 h-4 cursor-pointer"
                          title={areAllFilteredSelected() ? t('deselect_all') : t('select_all')}
                        />
                        <span className="text-gray-600 font-medium">
                          {t('symbols_found_count', { count: filteredSymbols().length })}
                        </span>
                      </div>
                    </div>
                    {filteredSymbols().map((item) => (
                      <div
                        key={item.symbol}
                        onClick={() => handleSymbolToggle(item.symbol)}
                        className={`
                          px-3 py-2 cursor-pointer hover:bg-blue-50 transition flex items-center gap-2
                          ${formData.symbols.includes(item.symbol) ? 'bg-blue-50' : ''}
                        `}
                      >
                        <input
                          type="checkbox"
                          checked={formData.symbols.includes(item.symbol)}
                          onChange={() => {}} // Handled by div onClick
                          className="w-4 h-4"
                        />
                        <div className="flex items-center justify-between flex-1">
                          <span className={`font-semibold text-gray-800 ${formData.symbols.includes(item.symbol) ? 'text-blue-600' : ''}`}>
                            {item.symbol}
                          </span>
                          <span className="text-xs text-gray-500 ml-2 truncate">{item.name}</span>
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="px-3 py-4 text-sm text-gray-500 text-center">
                    {t('no_symbols_found')}
                  </div>
                )}
              </div>
              
              {/* Selected Symbols Display */}
              <div className="mt-2">
                <div className="text-sm text-gray-600 mb-1">
                  {t('selected_count_label', { count: formData.symbols.length })}
                </div>
                <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                  {formData.symbols.map(symbol => (
                    <span 
                      key={symbol}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full"
                    >
                      {symbol}
                      <button
                        type="button"
                        onClick={() => handleSymbolToggle(symbol)}
                        className="hover:text-red-600"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Period */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('period')} * <span className="text-xs text-blue-600 font-normal">({t('period_recommended_hint')})</span>
              </label>
              <select
                name="period"
                value={formData.period}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {PERIODS.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {t('period_helper_text')}
              </p>
            </div>

            {/* Interval */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('interval')} * <span className="text-xs text-blue-600 font-normal">({t('interval_recommended_hint')})</span>
              </label>
              <select
                name="interval"
                value={formData.interval}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {INTERVALS.map(i => (
                  <option key={i.value} value={i.value}>{i.label}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {t('interval_helper_text')}
              </p>
            </div>

            {/* Account Size */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('account_size')}
              </label>
              <input
                type="number"
                name="account_size"
                value={formData.account_size}
                onChange={handleInputChange}
                min="1000"
                step="1000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Risk Percentage */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('risk_per_trade')}
              </label>
              <input
                type="number"
                name="risk_pct"
                value={formData.risk_pct}
                onChange={handleInputChange}
                min="0.1"
                max="10"
                step="0.1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">{t('risk_pct_hint')}</p>
            </div>

          </div>
        </div>
      </div>

      {/* Removed sticky bottom button; action moved beneath header */}
      </div>

      {/* Overlay for mobile */}
      {isMenuOpen && (
        <div
          onClick={() => setIsMenuOpen(false)}
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
        />
      )}

      {/* Save List Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">{`💾 ${t('save_symbol_list_title')}`}</h2>
            <p className="text-sm text-gray-600 mb-4">
              {t('save_symbol_list_description', { count: formData.symbols.length })}
            </p>
            <input
              type="text"
              placeholder={t('enter_list_name')}
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg mb-4 focus:ring-2 focus:ring-blue-500"
              autoFocus
              onKeyPress={(e) => e.key === 'Enter' && handleSaveList()}
            />
            <div className="flex gap-3">
              <button
                onClick={handleSaveList}
                className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
                disabled={!newListName.trim()}
              >
                {t('save')}
              </button>
              <button
                onClick={() => {
                  setShowSaveModal(false);
                  setNewListName('');
                }}
                className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400"
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScannerMenu;
